<template>
  <div class="text-module">
    <StudioTextEditor
      ref="textEditor"
      :id="id"
      :theme="theme"
      :theme-config="themeConfig"
      :background-style="style"
      :canvas-name="canvasName"
      :mode="mode"
      :module-active="active"
      :content="moduleData.options.content"
      :clip-contents="moduleData && moduleData.options && moduleData.options.shape
        && moduleData.options.shape !== 'default'"
      :data-type="moduleData.options.data_type"
      :text-placeholder="textPlaceholder"
      :scale="scale"
      :floating-menu="true"
      :overflow="moduleData.options.overflow"
      :disabled="isLocked(id)"
      :watch-content="textEditorShouldWatchForContentUpdates"
      :get-before-module-container-ref="getBeforeModuleContainerRef"
      :word-tracking="wordTracking"
      :parent-module-autosize-enabled="autoSizeEnabled(moduleData)"
      parent-module-can-rotate
      class="text-module__text-editor"
      :class="{'text-module__text-editor--size-to-parent': sizeToParent }"
      :page="page"
      @update="updateContent"
    />
  </div>
</template>

<script>
import { cloneDeep, get } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import * as types from '@/lib/constants/store';
import { getTemplatePlaceholders } from '@/lib/constants';
import { encodeHTML } from '@/lib/utils';
import autoSizeModule from '@/mixins/autoSizeModule';
import StudioTextEditor from '@/components/StudioTextEditor.vue';
import { themes } from '@/lib/constants/themes.json';
import {
  useEditorStore,
} from '@/stores';

export default {
  name: 'TextModule',
  components: {
    StudioTextEditor,
  },
  mixins: [autoSizeModule],
  props: {
    active: Boolean,
    id: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
    moduleData: {
      type: Object,
      required: true,
    },
    layoutData: {
      type: Object,
      default: null,
    },
    canvasName: {
      type: String,
      required: true,
    },
    scale: {
      type: Number,
      default: 1,
    },
    getBeforeModuleContainerRef: {
      type: Function,
      default: null,
    },
    sizeToParent: {
      type: Boolean,
      default: true,
    },
    page: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapState(useEditorStore, [
      'isLocked',
      'currentlyEditing',
    ]),
    theme() {
      const themeId = this.page?.options?.theme?.name;
      let theme = themes.find((t) => t.id === themeId);
      if (!theme && this.moduleTheme) {
        theme = themes.find((t) => t.id === 'default');
      }
      return theme;
    },
    moduleTheme() {
      return this.moduleData?.options?.theme;
    },
    themeConfig() {
      if (!this.moduleTheme) return null;
      return {
        textColors: {
          id: this.moduleTheme.name,
          variations: {
            id: this.moduleTheme.variation,
          },
        },
      };
    },
    style() {
      // New variation-based themes override the old style-based themes
      if (this.theme) return null;

      return {
        ...this.moduleData.options.style,
        backgroundColor: get(this, 'moduleData.options.style.background_color'),
      };
    },
    textPlaceholder() {
      // Template slides may contain placeholder text that needs to be localized,
      // which is wrapped with a $t{} syntax
      const placeholder = this.moduleData.options.text_placeholder;
      const placeholderKey = placeholder && placeholder.match(/\$t{(.*)}/);
      const templatePlaceholders = getTemplatePlaceholders();

      if (placeholderKey && placeholderKey[1] && templatePlaceholders[placeholderKey[1]]) {
        return placeholder.replace(/\$t{(.*)}/, templatePlaceholders[placeholderKey[1]]);
      }

      return placeholder;
    },
    textEditorShouldWatchForContentUpdates() {
      /*
        Text editor shouldn't watch for content updates if the current user is editing,
        otherwise cursor position won't be maintained properly.
      */
      return !get(this.currentlyEditing(this.id), 'isCurrentUser');
    },
    wordTracking() {
      if (this.canvasName === 'notes') return undefined;
      const wordTracking = get(this, 'moduleData.options.word_tracking');
      return wordTracking?.enabled ? wordTracking : undefined;
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.UPDATE_MODULE,
    ]),
    updateContent({ json }) {
      const data = cloneDeep(this.moduleData);
      data.options.content = encodeHTML(json);
      if (data.options.data_type) {
        delete data.options.data_type;
      }
      this[types.UPDATE_MODULE]({
        canvasName: this.canvasName,
        payload: data,
        captureTimeout: 500,
      });
    },
  },
};
</script>

<style lang="stylus">
.text-module {
  height: 100%;
  width: 100%;

  &__text-editor { // .text-module__text-editor
    border-radius: 6px; // match radius for other modules
    width: 100%;

    &--size-to-parent {
      height: 100%;
      position: absolute;
      top: 0;
    }
  }
}

</style>
